import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

const GLITCH_STYLES = `
@keyframes hs-glitch-skew {
  0%, 100% { transform: skewX(0deg); }
  92% { transform: skewX(0deg); }
  94% { transform: skewX(8deg); }
  96% { transform: skewX(-6deg); }
  98% { transform: skewX(3deg); }
}
@keyframes hs-glitch-pink {
  0%, 100% { clip-path: inset(0 0 0 0); transform: translate(0); }
  20% { clip-path: inset(12% 0 60% 0); transform: translate(-4px, -2px); }
  40% { clip-path: inset(70% 0 8% 0); transform: translate(4px, 2px); }
  60% { clip-path: inset(40% 0 38% 0); transform: translate(-3px, 1px); }
  80% { clip-path: inset(8% 0 78% 0); transform: translate(3px, -1px); }
}
@keyframes hs-glitch-blue {
  0%, 100% { clip-path: inset(0 0 0 0); transform: translate(0); }
  20% { clip-path: inset(64% 0 12% 0); transform: translate(4px, 2px); }
  40% { clip-path: inset(10% 0 72% 0); transform: translate(-4px, -2px); }
  60% { clip-path: inset(36% 0 42% 0); transform: translate(3px, -1px); }
  80% { clip-path: inset(78% 0 6% 0); transform: translate(-3px, 1px); }
}
@keyframes hs-glitch-flicker {
  0%, 100% { opacity: 1; }
  93% { opacity: 1; }
  94% { opacity: 0.4; }
  95% { opacity: 1; }
  97% { opacity: 0.7; }
  98% { opacity: 1; }
}
@keyframes hs-glitch-scan {
  0% { transform: translateY(-100%); opacity: 0; }
  20% { opacity: 0.1; }
  80% { opacity: 0.1; }
  100% { transform: translateY(100%); opacity: 0; }
}
.hs-glitch {
  position: relative;
  animation: hs-glitch-skew 4s infinite linear alternate;
}
.hs-glitch::before,
.hs-glitch::after {
  content: attr(data-text);
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.hs-glitch::before {
  color: var(--hs-pink);
  animation: hs-glitch-pink 2.6s infinite linear alternate-reverse;
  mix-blend-mode: screen;
}
.hs-glitch::after {
  color: var(--hs-blue);
  animation: hs-glitch-blue 3.1s infinite linear alternate-reverse;
  mix-blend-mode: screen;
}
.hs-glitch-flicker {
  animation: hs-glitch-flicker 5s infinite linear;
}
@media (prefers-reduced-motion: reduce) {
  .hs-glitch,
  .hs-glitch::before,
  .hs-glitch::after,
  .hs-glitch-flicker,
  .hs-glitch-scanline { animation: none; }
  .hs-glitch::before,
  .hs-glitch::after { display: none; }
}
`;

export interface GlitchSceneProps {
  heading: string;
  label: string;
  className?: string;
  headingClassName?: string;
  children?: ReactNode;
  "data-testid"?: string;
}

/** The 404 page's glitch treatment, reusable wherever a page-sized dead end
 *  needs the same brand moment. */
export function GlitchScene({
  heading,
  label,
  className,
  headingClassName,
  children,
  ...rest
}: GlitchSceneProps) {
  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center overflow-hidden bg-background px-6 text-center text-foreground",
        className,
      )}
      data-testid={rest["data-testid"]}
    >
      <style dangerouslySetInnerHTML={{ __html: GLITCH_STYLES }} />

      {/* ambient brand glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, color-mix(in srgb, var(--hs-pink) 18%, transparent), transparent 55%), radial-gradient(circle at 70% 70%, color-mix(in srgb, var(--hs-blue) 18%, transparent), transparent 55%)",
        }}
      />

      {/* drifting scanline */}
      <div
        aria-hidden
        className="hs-glitch-scanline pointer-events-none absolute inset-x-0 h-24 opacity-0"
        style={{
          background: "linear-gradient(to bottom, transparent, var(--hs-blue), transparent)",
          animation: "hs-glitch-scan 6s linear infinite",
        }}
      />

      <div className="relative z-10 flex flex-col items-center">
        <h1
          aria-label={heading}
          data-text={heading}
          className={cn(
            "hs-glitch select-none font-black leading-none tracking-tighter text-foreground",
            headingClassName ?? "text-[clamp(6rem,22vw,16rem)]",
          )}
        >
          {heading}
        </h1>

        <p className="hs-glitch-flicker mt-2 text-base font-medium uppercase tracking-[0.35em] text-muted-foreground">
          {label}
        </p>

        {children}
      </div>
    </div>
  );
}
